import { AnimatePresence, motion } from 'framer-motion';
import {
  Archive,
  ArrowLeft,
  BarChart2,
  Calendar,
  Heart,
  HelpCircle,
  Home as HomeIcon,
  Menu,
  MessageSquareHeart,
  Sparkles,
  User,
  X,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { markNewsSeen } from '../lib/news';
import { ThemeToggle } from './ThemeToggle';

export function NarrowPage({ children }: { children: ReactNode }) {
  return <div className="max-w-2xl mx-auto w-full">{children}</div>;
}

const MENU_ITEM =
  'flex items-center gap-3 p-3 rounded-xl hover:bg-muted transition-colors font-semibold text-left';

export function BrandLink({ className = '' }: { className?: string }) {
  return (
    <Link
      to="/"
      className={`p-1 rounded-lg hover:bg-muted transition-colors ${className}`}
      aria-label="Home"
    >
      <img src="/centumth logo black.png" alt="Centumth" className="h-7 w-auto dark:hidden" />
      <img src="/centumth logo white.png" alt="Centumth" className="h-7 w-auto hidden dark:block" />
    </Link>
  );
}

export function SiteMenuItems({
  onClose,
  newsUnreadCount = 0,
  howToPlay = 'link',
  onHowToPlayModal,
  statsMode = 'link',
  onOpenStats,
}: {
  onClose: () => void;
  newsUnreadCount?: number;
  howToPlay?: 'link' | 'modal';
  onHowToPlayModal?: () => void;
  statsMode?: 'link' | 'modal';
  onOpenStats?: () => void;
}) {
  return (
    <>
      <Link to="/play" onClick={onClose} className={MENU_ITEM}>
        <Calendar size={20} />
        Play Today
      </Link>
      <Link to="/archive" onClick={onClose} className={MENU_ITEM}>
        <Archive size={20} />
        Archive
      </Link>
      <Link
        to="/news"
        onClick={() => {
          markNewsSeen();
          onClose();
        }}
        className={MENU_ITEM}
      >
        <Sparkles size={20} />
        <span className="flex-1">News</span>
        {newsUnreadCount > 0 && (
          <span className="h-5 w-5 rounded-full bg-red-500 text-white text-[11px] font-black flex items-center justify-center">
            {newsUnreadCount}
          </span>
        )}
      </Link>
      {statsMode === 'modal' ? (
        <button
          type="button"
          onClick={() => {
            onClose();
            onOpenStats?.();
          }}
          className={MENU_ITEM}
        >
          <BarChart2 size={20} />
          Stats
        </button>
      ) : (
        <Link to="/play" state={{ openStats: true }} onClick={onClose} className={MENU_ITEM}>
          <BarChart2 size={20} />
          Stats
        </Link>
      )}
      <Link to="/you" onClick={onClose} className={MENU_ITEM}>
        <User size={20} />
        You
      </Link>
      <Link to="/about" onClick={onClose} className={MENU_ITEM}>
        <Heart size={20} />
        About us
      </Link>
      {howToPlay === 'modal' ? (
        <button
          type="button"
          onClick={() => {
            onClose();
            onHowToPlayModal?.();
          }}
          className={MENU_ITEM}
        >
          <HelpCircle size={20} />
          How to Play
        </button>
      ) : (
        <Link to="/how-to-play" onClick={onClose} className={MENU_ITEM}>
          <HelpCircle size={20} />
          How to Play
        </Link>
      )}
      <a
        href="https://tally.so/r/pbW6Wq"
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClose}
        className={MENU_ITEM}
      >
        <MessageSquareHeart size={20} />
        Leave Feedback
      </a>
    </>
  );
}

export function PageHeader({
  newsUnreadCount = 0,
  extra,
  howToPlay,
  onHowToPlayModal,
  statsMode,
  onOpenStats,
}: {
  newsUnreadCount?: number;
  extra?: ReactNode;
  howToPlay?: 'link' | 'modal';
  onHowToPlayModal?: () => void;
  statsMode?: 'link' | 'modal';
  onOpenStats?: () => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex justify-between items-center mb-10 relative">
      <BrandLink />
      <div className="flex items-center gap-2">
        {extra}
        <ThemeToggle />
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="relative p-2 rounded-full hover:bg-muted transition-colors"
          aria-label="Open menu"
        >
          <Menu size={24} />
          {newsUnreadCount > 0 && !open && (
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-[11px] font-black flex items-center justify-center">
              {newsUnreadCount}
            </span>
          )}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm"
              onClick={() => setOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.9 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="absolute top-full right-0 mt-2 z-50 bg-background border-2 border-border rounded-3xl shadow-2xl p-4 flex flex-col gap-2 w-[calc(100vw-2rem)] sm:w-80 origin-top-right"
            >
              <div className="flex justify-between items-center mb-2 px-2">
                <span className="font-bold text-lg">Menu</span>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="p-2 rounded-full hover:bg-muted transition-colors"
                  aria-label="Close menu"
                >
                  <X size={20} />
                </button>
              </div>
              <SiteMenuItems
                onClose={() => setOpen(false)}
                newsUnreadCount={newsUnreadCount}
                howToPlay={howToPlay}
                onHowToPlayModal={onHowToPlayModal}
                statsMode={statsMode}
                onOpenStats={onOpenStats}
              />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

const PILL =
  'px-3 py-1.5 rounded-full border border-border hover:bg-muted transition-colors text-sm font-semibold inline-flex items-center gap-1.5';

export function BackToToday() {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <Link to="/" className={PILL}>
        <HomeIcon size={14} />
        Home
      </Link>
      <Link to="/play" className={PILL}>
        <ArrowLeft size={14} />
        Today’s game
      </Link>
    </div>
  );
}
